import { useEffect, useMemo } from 'react';
import { getMessage } from '@tcc/i18n';
import { Icon } from '@tcc/crate';
import Card from '../../../components/cards/cardAndPreview/Card';
import PreviewCard from '../../../components/cards/cardAndPreview/PreviewCard';
import {
  getCropNominalWeight,
} from '../../../utils/processUnits';
import { isFieldYieldAnalysisFeatureAvailable } from '../../../selectors/fieldFeaturesSelectors';
import { switchView } from '../../../utils/url';
import { logDmbError } from '../../../utils/error';
import { trackEvent } from '../../../actions/analyticsActions';
import { HARVEST } from '../../../constants/mapLayerIds';
import { reqLegend } from '../../../actions/legendActions';
import { usernameSelector, userSelector } from '../../../selectors/userSelectors';
import { useAppDispatch, useAppSelector } from '../../../utils';
import { FeatureFlagKey, HarvestKey, ModalMode, ModalSize, ModalType } from '../../../types';
import {
  getActiveMapLayers,
  getActiveSeasons,
  getCurrentLayerId,
  getCurrentSeason,
  getField,
  getCurrentSeasonLayer,
  getLocale,
  getSeasonsList,
  getLayersState,
  getlegend,
  getHarvestState,
  getFeatureValue,
  getArdDataFeatureSelector
} from '../../../selectors';

import { HarvestInnerCard } from './HarvestInnerCard' ;
import { openModal } from '../../../actions/modalActions';

import styles from './HarvestCard.css';
import { SILAGE_CORN_CROP_CODE } from '../../../constants/cropCodes';

export const HARVEST_DATES_AMOUT_TO_SHOW = 3;

export type HarvestCardProps = {
  index: number;
  isPreview?: boolean;
};

const isHarvestData = (
  currentSeasonLayer: ReturnType<typeof getCurrentSeasonLayer>
): boolean => !!currentSeasonLayer?.[HarvestKey.DATE];

const isHarvestLayerActive = (
  activeLayer: string,
): boolean => Object.values(HarvestKey).includes(activeLayer as HarvestKey);

export const HarvestCard = ({ index, isPreview = false }: HarvestCardProps) => {
  const dispatch = useAppDispatch();

  const state = useAppSelector(state => state);
  const seasonsList = getSeasonsList(state);
  const harvestData = getHarvestState(state, { index }) ?? {};
  const { isFetching: isFetchingLegend } = getlegend(state);
  const activeSeasonId = getCurrentSeason(state, { index });
  const currentSeasonLayer = getCurrentSeasonLayer(state, { index });
  const activeMapLayers = getActiveMapLayers(state);
  const layersState = getLayersState(state);
  const username = usernameSelector(state);
  const field = getField(state);
  const user = userSelector(state);
  const locale = getLocale(state);
  const activeLayer = getCurrentLayerId(state, { index });
  const activeSeasons = getActiveSeasons(state);
  const isFieldYieldAnalysisAvailable = isFieldYieldAnalysisFeatureAvailable(state) as boolean;
  const isHarvestArdDataEnabled = getArdDataFeatureSelector(state);

  const hasStdYieldAccess = getFeatureValue(state, { key: FeatureFlagKey.DMB_STD_YIELD});

  const harvestDatesLegend = useMemo(() => currentSeasonLayer?.[HarvestKey.DATE]?.legend ?? [], [currentSeasonLayer]);
  const seasonEntry = seasonsList[activeSeasonId];
  const crop = seasonEntry?.crop;

  useEffect(() => {
    if (!seasonEntry) return;
    const { harvestEndDate, harvestStartDate, harvestYear, cropCustomName } = seasonEntry;
    const dates = {
      endDate: harvestEndDate,
      startDate: harvestStartDate,
      year: harvestYear,
    };

    

    if (isFetchingLegend || harvestDatesLegend.length || !isHarvestData(currentSeasonLayer)) {
      return;
    }
    dispatch(reqLegend(
      username,
      field.uuid.toUpperCase(),
      dates,
      cropCustomName,
      HarvestKey.DATE,
      activeSeasonId,
      undefined,
      user.uuid
    ));
  }, [
    activeSeasonId,
    harvestDatesLegend,
    isFetchingLegend,
    activeMapLayers,
    field.uuid,
    layersState?.list,
    seasonsList,
    seasonEntry,
    user.uuid,
    username,
    currentSeasonLayer,
    dispatch
  ]);

  const openYieldMap = () => {
    if (activeLayer === HarvestKey.YIELD) {
      return;
    }

    try {
      const seasonIds = activeSeasons.map((season) => season.id);
      const layerIds = activeMapLayers.map((layer) => layer.id);
      switchView(index, activeSeasonId, seasonIds, HARVEST.YIELD, layerIds, field.id ?? '');

      dispatch(trackEvent('harvest-view-yield-link', 'clicked'));
    } catch (error) {
      logDmbError(error, {
        appLocation: 'harvest view yield map selector',
        seasonId: activeSeasonId,
      });
    }
  };

  const displayAdditionalSilageData = hasStdYieldAccess && crop === SILAGE_CORN_CROP_CODE;
  const harvestDateFiltered = useMemo(() =>
      harvestDatesLegend.filter(date => !!date.stringValue), [harvestDatesLegend]);

  const isYieldLayersSelected = activeMapLayers.some(layer => layer.id === HARVEST.YIELD)
  const showYieldMapLink = harvestDateFiltered.length > 0 && !isYieldLayersSelected;
  const yieldMapLink = showYieldMapLink ? getMessage('card.harvest.viewYieldMap') : '';

  if (!seasonEntry) {
    return null;
  }

  const innerCard = (
    <HarvestInnerCard
      cropCode={crop}
      isFieldYieldAnalysisAvailable={isFieldYieldAnalysisAvailable}
      cropNominalWeight={getCropNominalWeight(crop, state.crops)}
      harvestData={harvestData}
      displayAdditionalSilageData={displayAdditionalSilageData}
      isHarvestArdDataEnabled={isHarvestArdDataEnabled}
      harvestDates={{
        showListOfDates: isHarvestLayerActive(activeLayer),
        harvestDatesLegend: harvestDateFiltered,
        locale,
      }}
    />
  );
  const testId = 'dmb-harvest';

  if (isPreview) {
    return <PreviewCard testId={testId}>{innerCard}</PreviewCard>;
  }

  return (
    <Card
      cardType="harvest"
      link={yieldMapLink ?? undefined}
      onClickLink={openYieldMap}
      testId={testId}
      title={getMessage('layer.harvest')}
      icon={displayAdditionalSilageData ? Icon.set.INFO_CIRCLE : undefined}
      iconClassName={displayAdditionalSilageData ? styles.seeFormulasIcon : ''}
      onClickIcon={() => {
        if (displayAdditionalSilageData) {
          dispatch(openModal(index, null as never, {
            type: ModalType.SILAGE,
            mode: ModalMode.SHOW_SILAGE_FORMULAS,
            size: ModalSize.L
          }));
        }
      }}
      >
        {innerCard}
    </Card>
  );
};
